// Token Service for managing token data and metadata
class TokenService {
  constructor() {
    this.tokenCache = new Map();
    this.priceCache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  // Popular Solana tokens with metadata
  getPopularTokens() {
    return [
      {
        mint: 'So11111111111111111111111111111111111111112',
        symbol: 'SOL',
        name: 'Solana',
        decimals: 9,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
        coingeckoId: 'solana'
      },
      {
        mint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
        symbol: 'USDC',
        name: 'USD Coin',
        decimals: 6,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png',
        coingeckoId: 'usd-coin'
      },
      {
        mint: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
        symbol: 'USDT',
        name: 'Tether USD',
        decimals: 6,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB/logo.png',
        coingeckoId: 'tether'
      },
      {
        mint: 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So',
        symbol: 'mSOL',
        name: 'Marinade Staked SOL',
        decimals: 9,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So/logo.png',
        coingeckoId: 'marinade-staked-sol'
      },
      {
        mint: '7dHbWXmci3dT8UFYWYZweBLXgycu7Y3iL6trKn1Y7ARj',
        symbol: 'stSOL',
        name: 'Lido Staked SOL',
        decimals: 9,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/7dHbWXmci3dT8UFYWYZweBLXgycu7Y3iL6trKn1Y7ARj/logo.png',
        coingeckoId: 'lido-staked-sol'
      },
      {
        mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263',
        symbol: 'BONK',
        name: 'Bonk',
        decimals: 5,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263/logo.png',
        coingeckoId: 'bonk'
      },
      {
        mint: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN',
        symbol: 'JUP',
        name: 'Jupiter',
        decimals: 6,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN/logo.png',
        coingeckoId: 'jupiter-exchange-solana'
      },
      {
        mint: 'WENWENvqqNya429ubCdR81ZmD69brwQaaBYY6p3LCpk',
        symbol: 'WEN',
        name: 'Wen',
        decimals: 5,
        logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/WENWENvqqNya429ubCdR81ZmD69brwQaaBYY6p3LCpk/logo.png',
        coingeckoId: 'wen-4'
      }
    ];
  }

  async fetchJupiterToken(mintAddress) {
    const url = `https://tokens.jup.ag/token/${mintAddress}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch Jupiter token: ${res.status}`);
    return await res.json();
  }

  async getTokenMetadata(mintAddress) {
    // Check cache first
    const cached = this.tokenCache.get(mintAddress);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }

    // Check if it's a popular token
    const popularTokens = this.getPopularTokens();
    const popularToken = popularTokens.find(token => token.mint === mintAddress);
    
    if (popularToken) {
      this.tokenCache.set(mintAddress, {
        data: popularToken,
        timestamp: Date.now()
      });
      return popularToken;
    }

    // Try Jupiter token registry, then enrich with DexScreener if needed
    let baseMeta = null;
    try {
      const jt = await this.fetchJupiterToken(mintAddress);
      baseMeta = {
        mint: jt.address || mintAddress,
        symbol: jt.symbol || 'UNKNOWN',
        name: jt.name || 'Unknown Token',
        decimals: typeof jt.decimals === 'number' ? jt.decimals : 6,
        logoURI: jt.logoURI || '/images/unknown-token.png',
        coingeckoId: jt.extensions?.coingeckoId || undefined
      };
    } catch (e) {
      baseMeta = {
        mint: mintAddress,
        symbol: 'UNKNOWN',
        name: 'Unknown Token',
        decimals: 6,
        logoURI: '/images/unknown-token.png'
      };
    }

    try {
      const dex = await this.getDexTokenData(mintAddress);
      if (dex) {
        baseMeta = {
          ...baseMeta,
          symbol: baseMeta.symbol === 'UNKNOWN' && dex.symbol ? dex.symbol : baseMeta.symbol,
          name: (baseMeta.name === 'Unknown Token' && dex.name) ? dex.name : baseMeta.name,
          logoURI: baseMeta.logoURI, // Dex often lacks logos; keep existing
        };
      }
    } catch (e) {
      // ignore enrichment failures
    }

    this.tokenCache.set(mintAddress, { data: baseMeta, timestamp: Date.now() });
    return baseMeta;
  }

  async getTokenPrice(identifier) {
    const key = String(identifier || 'solana');
    const cached = this.priceCache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }

    // Prefer Jupiter price API by mint if input looks like a mint address
    const isMint = typeof identifier === 'string' && identifier.length === 44;
    const ids = encodeURIComponent(key);
    const url = `https://price.jup.ag/v6/price?ids=${ids}`;
    try {
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        const data = json.data?.[key] || Object.values(json.data || {})[0] || null;
        if (data) {
          const normalized = {
            price: data.price ?? null,
            change24h: data.priceChange24h ?? null,
            marketCap: data.marketCap ?? null,
            confidence: data.confidence ?? null
          };
          this.priceCache.set(key, { data: normalized, timestamp: Date.now() });
          return normalized;
        }
      }
    } catch (e) {
      // ignore, fallback below
    }

    // Fallback minimal structure
    const fallback = { price: null, change24h: null, marketCap: null };
    this.priceCache.set(key, { data: fallback, timestamp: Date.now() });
    return fallback;
  }

  // DexScreener data used in SolanaTokensDashboard
  async getDexTokenData(mintAddress) {
    const cacheKey = `dex:${mintAddress}`;
    const cached = this.priceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }

    try {
      const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${mintAddress}`);
      const data = await res.json();
      if (data.pairs && data.pairs.length > 0) {
        const bestPair = data.pairs.reduce((best, current) => {
          if (!best) return current;
          const curLiq = current.liquidity?.usd || 0;
          const bestLiq = best.liquidity?.usd || 0;
          return curLiq > bestLiq ? current : best;
        });
        const normalized = {
          price: parseFloat(bestPair.priceUsd) || null,
          change24h: parseFloat(bestPair.priceChange?.h24) || null,
          volume24h: parseFloat(bestPair.volume?.h24) || null,
          marketCap: parseFloat(bestPair.fdv) || null,
          symbol: bestPair.baseToken?.symbol || undefined,
          name: bestPair.baseToken?.name || undefined,
          // DexScreener does not always provide logos; keep undefined to allow fallback
          logoURI: undefined
        };
        this.priceCache.set(cacheKey, { data: normalized, timestamp: Date.now() });
        return normalized;
      }
    } catch (e) {
      // ignore
    }
    const fallback = { price: null, change24h: null, volume24h: null, marketCap: null };
    this.priceCache.set(cacheKey, { data: fallback, timestamp: Date.now() });
    return fallback;
  }

  // Search tokens by symbol or name
  searchTokens(query) {
    const popularTokens = this.getPopularTokens();
    const lowercaseQuery = query.toLowerCase();
    
    return popularTokens.filter(token =>
      token.symbol.toLowerCase().includes(lowercaseQuery) ||
      token.name.toLowerCase().includes(lowercaseQuery)
    );
  }

  // Validate if a mint address is valid format
  isValidMintAddress(address) {
    // Basic Solana address validation (44 characters, base58)
    if (!address || address.length !== 44) return false;
    
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
  }

  // Format token amount with proper decimals
  formatTokenAmount(amount, decimals = 6, maxDecimals = 6) {
    const value = amount / Math.pow(10, decimals);
    
    if (value === 0) return '0';
    if (value < 0.000001) return '<0.000001';
    if (value < 1) return value.toFixed(maxDecimals);
    if (value < 1000) return value.toFixed(Math.min(4, maxDecimals));
    if (value < 1000000) return (value / 1000).toFixed(2) + 'K';
    if (value < 1000000000) return (value / 1000000).toFixed(2) + 'M';
    return (value / 1000000000).toFixed(2) + 'B';
  }

  // Format price with appropriate precision
  formatPrice(price) {
    if (price === 0) return '$0.00';
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    if (price < 1000) return `$${price.toFixed(2)}`;
    if (price < 1000000) return `$${(price / 1000).toFixed(1)}K`;
    return `$${(price / 1000000).toFixed(1)}M`;
  }

  // Clear cache (useful for testing or manual refresh)
  clearCache() {
    this.tokenCache.clear();
    this.priceCache.clear();
  }
}

// Export singleton instance
export default new TokenService();